import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categoryData } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex items-center justify-between h-16 px-4 md:px-6" style={{ maxWidth: 1200 }}>
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-table-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-xl font-bold text-foreground-950 whitespace-nowrap">
              GTBuy Hub
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              GTBuy Sheet
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  {categoryData.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-foreground-400 text-sm`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/gtbuy-sheet-guide"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Guide
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line text-sm"></i>
              Buy It Now
            </a>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-foreground-950 text-xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeMobileMenu}
          ></div>
          <div className="absolute top-16 left-0 right-0 bg-background-50 border-b border-background-200/70 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-foreground-400 text-sm"></i>
                </div>
                GTBuy Sheet
              </a>

              <div className="pt-2 pb-1">
                <span className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</span>
              </div>

              {categoryData.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${cat.icon} text-foreground-400 text-sm`}></i>
                  </div>
                  {cat.name}
                </a>
              ))}

              <div className="border-t border-background-200/70 my-2"></div>

              <Link
                to="/gtbuy-sheet-guide"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-open-line text-foreground-400 text-sm"></i>
                </div>
                Guide
              </Link>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 rounded-lg transition-colors mt-2"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-background-50 text-sm"></i>
                </div>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}